import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Ship, LogOut, Calendar, Clock, Users, MapPin, 
  Phone, RefreshCw, ChevronRight, Anchor, Mountain,
  Waves, Fish
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tour type icons
const getTourIcon = (tourId) => {
  if (tourId.includes("corcovado") || tourId.includes("sirena")) {
    return <Mountain className="w-5 h-5" />;
  } else if (tourId.includes("snorkeling") || tourId.includes("mangrove")) {
    return <Waves className="w-5 h-5" />;
  } else if (tourId.includes("scuba") || tourId.includes("dive")) {
    return <Fish className="w-5 h-5" />;
  }
  return <Anchor className="w-5 h-5" />;
};

// Tour type colors
const getTourColor = (tourId) => {
  if (tourId.includes("corcovado") || tourId.includes("sirena")) {
    return "bg-green-600";
  } else if (tourId.includes("snorkeling") || tourId.includes("mangrove")) {
    return "bg-blue-500";
  } else if (tourId.includes("scuba") || tourId.includes("dive")) {
    return "bg-cyan-600";
  }
  return "bg-stone-600";
};

export default function TourStaffDashboard() {
  const navigate = useNavigate();
  const [calendarData, setCalendarData] = useState([]);
  const [todayTours, setTodayTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("today");

  useEffect(() => {
    // Check auth
    if (!sessionStorage.getItem("tourStaffAuth")) {
      navigate("/tourstaff");
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load today's tours and calendar data in parallel
      const [todayRes, calendarRes] = await Promise.all([
        fetch(`${API_URL}/api/tourstaff/tours/today`),
        fetch(`${API_URL}/api/tourstaff/calendar`)
      ]);
      
      if (todayRes.ok) {
        const data = await todayRes.json();
        setTodayTours(data);
      }
      
      if (calendarRes.ok) {
        const data = await calendarRes.json();
        setCalendarData(data);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Error loading tour data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("tourStaffAuth");
    navigate("/tourstaff");
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.getTime() === today.getTime()) {
      return "Today";
    } else if (date.getTime() === tomorrow.getTime()) {
      return "Tomorrow";
    }
    
    return date.toLocaleDateString("en-US", { 
      weekday: "short", 
      month: "short", 
      day: "numeric" 
    });
  };

  const TourCard = ({ tour, showDate = false }) => (
    <Card 
      className={`bg-stone-100 border-l-4 ${getTourColor(tour.tour_id)} border-t-0 border-r-0 border-b-0`}
      data-testid={`tour-card-${tour.id}`}
    >
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-full ${getTourColor(tour.tour_id)} text-white`}>
              {getTourIcon(tour.tour_id)}
            </div>
            <div>
              <h3 className="font-semibold text-stone-800">{tour.tour_name}</h3>
              {showDate && (
                <p className="text-sm text-stone-500">{formatDate(tour.date)}</p>
              )}
            </div>
          </div>
          <Badge 
            className={tour.status === "confirmed" ? "bg-green-600" : "bg-yellow-500"}
          >
            {tour.status}
          </Badge>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-stone-700">
            <Clock className="w-4 h-4 text-primary" />
            <span className="font-medium">Pickup: {tour.pickup_time || "TBD"}</span>
          </div>
          
          <div className="flex items-center gap-2 text-stone-700">
            <Users className="w-4 h-4 text-stone-500" />
            <span>{tour.guest_name}</span>
            <span className="text-stone-500">({tour.num_participants} people)</span>
          </div>
          
          <div className="flex items-center gap-2 text-stone-600">
            <MapPin className="w-4 h-4 text-stone-500" />
            <span>{tour.cabin_name} • {tour.hotel_name}</span>
          </div>
          
          {tour.phone_number && (
            <div className="flex items-center gap-2 text-stone-600">
              <Phone className="w-4 h-4 text-stone-500" />
              <a href={`tel:${tour.phone_number}`} className="text-primary hover:underline">
                {tour.phone_number}
              </a>
            </div>
          )}
          
          {tour.participant_names && tour.participant_names.length > 0 && (
            <div className="bg-stone-200/50 rounded p-2 mt-2">
              <p className="text-xs text-stone-500 mb-1">Participants:</p>
              <p className="text-stone-700">{tour.participant_names.join(", ")}</p>
            </div>
          )}
          
          {tour.special_requests && (
            <div className="bg-yellow-100 rounded p-2 mt-2">
              <p className="text-xs text-yellow-700">Note: {tour.special_requests}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-stone-300">
      {/* Header */}
      <header className="bg-stone-500 text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <Ship className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold">Tour Pickups</h1>
              <p className="text-stone-200 text-sm">Guest Coordination</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={loadData}
              className="text-white hover:bg-stone-600"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-white hover:bg-stone-600"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-stone-400 mb-6">
            <TabsTrigger 
              value="today" 
              className="data-[state=active]:bg-primary data-[state=active]:text-white"
              data-testid="today-tab"
            >
              Today's Tours
            </TabsTrigger>
            <TabsTrigger 
              value="calendar"
              className="data-[state=active]:bg-primary data-[state=active]:text-white"
              data-testid="calendar-tab"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Calendar
            </TabsTrigger>
          </TabsList>

          {/* Today's Tours Tab */}
          <TabsContent value="today">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="bg-stone-100 border-stone-300 animate-pulse">
                    <CardContent className="p-6">
                      <div className="h-6 bg-stone-300 rounded w-1/3 mb-4"></div>
                      <div className="h-4 bg-stone-300 rounded w-2/3 mb-2"></div>
                      <div className="h-4 bg-stone-300 rounded w-1/2"></div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : todayTours.length === 0 ? (
              <Card className="bg-stone-100 border-stone-300">
                <CardContent className="p-12 text-center">
                  <Ship className="w-12 h-12 mx-auto mb-4 text-stone-400" />
                  <h3 className="text-lg font-semibold text-stone-700 mb-2">No Tours Today</h3>
                  <p className="text-stone-500">Check the calendar for upcoming tours</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-display text-xl font-bold text-stone-800">
                    {new Date().toLocaleDateString("en-US", { 
                      weekday: "long", 
                      month: "long", 
                      day: "numeric" 
                    })}
                  </h2>
                  <Badge className="bg-primary">{todayTours.length} tours</Badge>
                </div>
                {todayTours.map((tour) => (
                  <TourCard key={tour.id} tour={tour} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            {loading ? (
              <div className="space-y-6">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-6 bg-stone-400 rounded w-1/4 mb-4"></div>
                    <Card className="bg-stone-100 border-stone-300">
                      <CardContent className="p-6">
                        <div className="h-4 bg-stone-300 rounded w-2/3 mb-2"></div>
                        <div className="h-4 bg-stone-300 rounded w-1/2"></div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            ) : calendarData.length === 0 ? (
              <Card className="bg-stone-100 border-stone-300">
                <CardContent className="p-12 text-center">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-stone-400" />
                  <h3 className="text-lg font-semibold text-stone-700 mb-2">No Upcoming Tours</h3>
                  <p className="text-stone-500">Tours will appear here when booked</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {calendarData.map((dayData) => (
                  <div key={dayData.date}>
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar className="w-5 h-5 text-primary" />
                      <h2 className="font-display text-lg font-bold text-stone-800">
                        {formatDate(dayData.date)}
                      </h2>
                      <Badge variant="outline" className="border-stone-400 text-stone-600">
                        {dayData.tours.length} tours
                      </Badge>
                    </div>
                    <div className="space-y-3 pl-7">
                      {dayData.tours.map((tour) => (
                        <TourCard key={tour.id} tour={{...tour, date: dayData.date}} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
